import { useState, type FormEvent } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { FoiRecord } from '../../lib/types';
import { ErrorNote, Field, PageHeader, StatusBadge } from '../../ui/components';

/**
 * There is no "list open FOI requests" endpoint in c11-opengov (see
 * routes/foi.ts) — only submit, the public per-ref status read, and the
 * assign/respond actions. So the staff console can only act on a request
 * once it already has the reference number (e.g. quoted back to the officer
 * in the citizen's own enquiry, or forwarded internally). This page is a
 * deliberate "look up one, act on it" tool rather than a queue — a real
 * queue would need a new auth-gated list endpoint on the module, which is
 * out of scope here (read-only reference).
 */
export function StaffFoi() {
  const { persona } = useAuth();
  const isFoiOfficer = persona?.roles.includes('FOI_OFFICER') ?? false;

  const [ref, setRef] = useState('');
  const [foi, setFoi] = useState<FoiRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [responseSummary, setResponseSummary] = useState('');

  const lookup = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!ref.trim()) return;
    setLoading(true);
    setError(null);
    setActionError(null);
    try {
      setFoi(await api.get<FoiRecord>(`/api/v1/opengov/foi/${encodeURIComponent(ref.trim())}`));
    } catch (e) {
      setFoi(null);
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const assignToMe = async () => {
    if (!foi) return;
    setActionBusy(true);
    setActionError(null);
    try {
      const updated = await api.post<FoiRecord>(`/api/v1/opengov/foi/${encodeURIComponent(foi.refNo)}/assign`, {
        assigneeId: persona?.id ?? persona?.name ?? 'staff',
      });
      setFoi(updated);
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not assign request');
    } finally {
      setActionBusy(false);
    }
  };

  const respond = async () => {
    if (!foi || responseSummary.trim().length < 3) return;
    setActionBusy(true);
    setActionError(null);
    try {
      const updated = await api.post<FoiRecord>(`/api/v1/opengov/foi/${encodeURIComponent(foi.refNo)}/respond`, {
        responseSummary: responseSummary.trim(),
      });
      setFoi(updated);
      setResponseSummary('');
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not respond to request');
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="FOI requests" subtitle="Look up a request by reference number to assign or respond to it." />

      {!isFoiOfficer ? (
        <div className="mb-4">
          <ErrorNote message="Your account does not hold the FOI_OFFICER role — you can look up a request, but assign/respond actions will be rejected by the API." />
        </div>
      ) : null}

      <div className="card mb-4 p-4">
        <form onSubmit={lookup} className="flex items-end gap-2">
          <Field label="FOI reference number" className="flex-1">
            <input
              className="field-input"
              placeholder="e.g. FOI-2026-MOH0001"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
            />
          </Field>
          <button className="btn-primary" disabled={!ref.trim() || loading}>
            {loading ? 'Looking up…' : 'Look up'}
          </button>
        </form>
        <p className="mt-2 text-xs text-slate-400">
          There is no request queue in C11 OpenGov yet — requests are looked up one at a time by the reference
          number quoted in the citizen's enquiry.
        </p>
      </div>

      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      {foi ? (
        <div className="card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-mono text-xs font-semibold text-slate-400">{foi.refNo}</div>
              <h2 className="text-base font-bold text-slate-900">{foi.subject}</h2>
            </div>
            <StatusBadge status={foi.state} />
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Target MDA</div>
              <div className="mt-0.5 text-sm font-semibold text-slate-900">{foi.targetMda}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Statutory due date</div>
              <div className={`mt-0.5 text-sm font-semibold ${foi.overdue ? 'text-rose-600' : 'text-slate-900'}`}>
                {formatDate(foi.statutoryDueAt)}
                {foi.overdue ? ' — overdue' : ''}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Received</div>
              <div className="mt-0.5 text-sm font-semibold text-slate-900">{formatDateTime(foi.createdAt)}</div>
            </div>
          </div>

          {foi.assigneeId ? (
            <p className="mt-3 text-xs text-slate-500">
              Assigned to <span className="font-semibold text-slate-700">{foi.assigneeId}</span>
              {foi.assignedAt ? ` on ${formatDateTime(foi.assignedAt)}` : ''}.
            </p>
          ) : null}
          {foi.responseSummary ? (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <span className="font-semibold">Response on file: </span>
              {foi.responseSummary}
            </div>
          ) : null}

          {actionError ? (
            <div className="mt-3">
              <ErrorNote message={actionError} />
            </div>
          ) : null}

          {foi.state === 'RECEIVED' ? (
            <button className="btn-primary mt-4" disabled={actionBusy} onClick={assignToMe}>
              {actionBusy ? 'Assigning…' : 'Assign to me'}
            </button>
          ) : null}

          {foi.state === 'ASSIGNED' ? (
            <div className="mt-4 space-y-2">
              <Field label="Response summary">
                <textarea
                  className="field-input"
                  rows={3}
                  value={responseSummary}
                  onChange={(e) => setResponseSummary(e.target.value)}
                  placeholder="Summarise the information released (and any redactions), for the public record."
                />
              </Field>
              <button className="btn-primary" disabled={actionBusy || responseSummary.trim().length < 3} onClick={respond}>
                {actionBusy ? 'Submitting…' : 'Submit response'}
              </button>
            </div>
          ) : null}

          {foi.state === 'RESPONDED' || foi.state === 'APPEALED' ? (
            <p className="mt-4 text-xs text-slate-400">
              {foi.state === 'APPEALED'
                ? 'This request is under appeal — no further staff action is exposed by this API.'
                : 'This request has been responded to. If the requester appeals, it will move to APPEALED.'}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
